/**
 * K108-X — owner/admin "Training Needs Review" queue.
 *
 * Owner/admin/manager ONLY (normal technicians never see it). Lists unresolved
 * attestation mismatches for the caller's shop and lets a reviewer record a neutral
 * decision via the review_training_session RPC. Language is neutral throughout —
 * never accusatory, never "verified completion".
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Eye, Ticket, User } from 'lucide-react';
import { toast } from 'sonner';
import { useUserRole } from '@/hooks/useUserRole';
import { useTrainingNeedsReview, useReviewTrainingSession } from '@/hooks/useTrainingNeedsReview';
import { REVIEW_ACTIONS, reviewActionRequiresNote, type ReviewAction } from '@/lib/training/trainingNeedsReview';

function fmt(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'MMM d, yyyy h:mm a');
}

export function TrainingNeedsReviewQueue() {
  const roleInfo = useUserRole();
  // Owner/admin/manager only — technicians never see the queue or actions.
  const canReview =
    !!roleInfo && (roleInfo.isManagerView || roleInfo.role === 'ORG_ADMIN' || roleInfo.role === 'OWNER');

  const { items, count } = useTrainingNeedsReview(canReview);
  const review = useReviewTrainingSession();
  const [notes, setNotes] = useState<Record<string, string>>({});

  if (!canReview || count === 0) return null;

  const submit = (sessionId: string, action: ReviewAction, observedPercent: number | null) => {
    const note = (notes[sessionId] ?? '').trim();
    if (reviewActionRequiresNote(action) && !note) {
      toast.error('A note is required to request a retake / follow-up.');
      return;
    }
    review.mutate(
      { sessionId, action, note: note || null, observedPercent },
      {
        onSuccess: () => toast.success('Review recorded.'),
        onError: (err) => {
          const reason = err instanceof Error ? err.message : String(err);
          toast.error(
            import.meta.env?.DEV
              ? `Could not record the review: ${reason}`
              : 'Could not record the review. Please try again.',
          );
        },
      },
    );
  };

  return (
    <Card data-testid="training-needs-review-queue" className="border-amber-300">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          Training Needs Review
          <Badge variant="destructive" data-testid="needs-review-count">
            {count}
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Owner/admin review required. Needs review because the user report differs from monitored
          engagement — monitoring may be incomplete, or the training may have been completed elsewhere.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((item) => (
          <div key={item.sessionId} data-testid="needs-review-item" className="rounded-lg border p-3 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium truncate">{item.artifactTitle}</p>
                {item.provider && <p className="text-xs text-muted-foreground">{item.provider}</p>}
              </div>
              {item.ticketDisplay && (
                <Badge variant="outline" className="flex-shrink-0 gap-1">
                  <Ticket className="h-3 w-3" />#{item.ticketDisplay}
                </Badge>
              )}
            </div>

            <div className="space-y-0.5 text-xs text-muted-foreground">
              <p className="flex items-center gap-1">
                <User className="h-3 w-3 flex-shrink-0" />
                {item.technicianName}
              </p>
              <p className="flex items-center gap-1">
                <Eye className="h-3 w-3 flex-shrink-0" />
                {item.observedLine}
                {item.confidence ? ` · ${item.confidence} engagement` : ''}
              </p>
              {item.userReportedLabel && <p>You reported: {item.userReportedLabel}</p>}
              {item.lastActivityAt && <p>Last activity: {fmt(item.lastActivityAt)}</p>}
            </div>

            <Textarea
              data-testid="needs-review-note"
              value={notes[item.sessionId] ?? ''}
              onChange={(e) => setNotes((n) => ({ ...n, [item.sessionId]: e.target.value }))}
              placeholder="Add a note (required to request a retake)…"
              rows={2}
              className="text-sm"
            />

            <div className="flex flex-wrap gap-2">
              {REVIEW_ACTIONS.map((a) => (
                <Button
                  key={a.action}
                  size="sm"
                  variant={a.action === 'accept_report' ? 'default' : 'outline'}
                  disabled={review.isPending}
                  onClick={() => submit(item.sessionId, a.action, item.monitoredPercent)}
                >
                  {a.label}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
